// ============================================================================
// OBT F3 — Yorum/gerekçe gerektiren inceleme işlemi diyaloğu
// ============================================================================
// Revizyon isteme (§10) ve reddetme gibi ZORUNLU gerekçeli işlemler için ortak
// diyalog. Çift işlem `submitting` ile engellenir. Backend nihai otoritedir;
// bu diyalog yalnız erken doğrulama + kullanıcı onayı sağlar.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { MAX_COMMENT_LEN, isRequiredCommentValid } from './reviewValidation';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onOpenChange
 * @param {(comment: string) => Promise<void>} props.onConfirm  hata fırlatırsa diyalog açık kalır
 * @param {string} props.title
 * @param {string} props.description       onay uyarısı (ör. "Formene geri gönderilecek")
 * @param {string} [props.confirmLabel]
 * @param {string} [props.commentLabel]
 * @param {string} [props.placeholder]
 * @param {string|null} [props.error]      normalize edilmiş hata mesajı
 */
export default function RevisionRequestDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Gönder',
  commentLabel = 'Gerekçe',
  placeholder = 'Formene iletilecek düzeltme gerekçesini yazın…',
  error = null,
}) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setComment('');
      setSubmitting(false);
    }
  }, [open]);

  const valid = isRequiredCommentValid(comment);

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(comment.trim());
    } catch {
      // Hata üst bileşende normalize edilip `error` ile gösterilir; diyalog açık.
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="review-comment">{commentLabel} *</Label>
          <Textarea
            id="review-comment"
            value={comment}
            maxLength={MAX_COMMENT_LEN}
            placeholder={placeholder}
            rows={4}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
          />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Vazgeç
          </Button>
          <Button onClick={handleConfirm} disabled={!valid || submitting}>
            {submitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
